import { LoadingOutlined } from '@ant-design/icons'
import {
  Button,
  Collapse,
  CollapseProps,
  Divider,
  message,
  Spin,
  Typography,
} from 'antd'
import { useIndicationData } from 'features/indicationTriage/IndicationContext'
import { IndicationDetailCardProps } from 'features/indicationTriage/useIndicationDetailCards'
import { useRolodexFileLS, useRolodexSignedURLs } from 'features/rolodex'
import _ from 'lodash'
import React, { FC, useMemo } from 'react'
import styled from 'styled-components'
import { GeckoTheme } from 'styles/GeckoTheme'
import useSWRImmutable from 'swr/immutable'

import WaterfallPlot from './trilatReportSignalCharts/WaterfallPlot'

const { Text, Title: AntTitle } = Typography

const fileFetcher = async (url: string): Promise<number[][]> => {
  const res = fetch(url).then((resp) => resp.json())
  return res
}

const formatPath = (path: string | undefined) => {
  if (path) {
    return path.split('.')[0]
  }
  return ''
}

const useIndicationFiles = (indicationId: string | undefined) => {
  const { fileRefs } = useRolodexFileLS(
    useMemo(
      () =>
        indicationId
          ? {
              entry_id: indicationId,
              path: '/*',
            }
          : undefined,
      [indicationId]
    )
  )

  const { urls, isLoading: rolodexLoading } = useRolodexSignedURLs(fileRefs)

  const allFetch = React.useCallback(
    () =>
      Promise.all(
        urls.map(async (url, index) => ({
          data: await fileFetcher(url),
          filename: formatPath(fileRefs[index].path),
        }))
      ),
    [fileRefs, urls]
  )

  const { data, isLoading } = useSWRImmutable([urls], allFetch)

  const formattedData: Record<string, number[][]> = data
    ? data.reduce((acc: Record<string, number[][]>, current) => {
        acc[current.filename] = current.data
        return acc
      }, {})
    : {}
  return { data: formattedData, isLoading: isLoading && rolodexLoading }
}

type DRAViewProps = {
  bscan0: number[][]
  bscan1: number[][]
  bscan2: number[][]
}

const DRA1View: FC<DRAViewProps> = (props) => {
  const { bscan0, bscan2 } = props

  const items: CollapseProps['items'] = [
    {
      key: '1',
      label: 'No backwall response in the 0°',
      children: <DRA1Item1 data={bscan0}></DRA1Item1>,
    },
    {
      key: '2',
      label: 'Connection to inner diameter in the 45°',
      children: <DRA1Item2 data={bscan2}></DRA1Item2>,
    },
  ]

  return (
    <ColumnWrapper>
      <AntTitle level={4}>Observed Qualities</AntTitle>
      <Collapse items={items} accordion />
      <Divider></Divider>
      <DRA1VerdictItem></DRA1VerdictItem>
      <Divider></Divider>
      <DRA1Actions></DRA1Actions>
    </ColumnWrapper>
  )
}

const DRA1Item1: FC<{ data: number[][] }> = (props) => {
  const { data } = props

  const shape1 = {
    type: 'rect' as const,
    x0: 19,
    x1: 29,
    y0: 238,
    y1: 253,
    line: { color: GeckoTheme.colors.SeaGreen },
  }

  const shape2 = {
    type: 'line' as const,
    x0: 0,
    x1: data ? data.length : 0,
    y0: 205,
    y1: 205,
    line: { color: GeckoTheme.colors.BrightOrange },
  }

  return (
    <div>
      <WaterfallPlot
        dataValues={data}
        width={320}
        height={500}
        shapes={[shape1, shape2]}></WaterfallPlot>
      <Text>
        <ColorSpan color={GeckoTheme.colors.SeaGreen}>The blister </ColorSpan>
        has{' '}
        <ColorSpan color={GeckoTheme.colors.BrightOrange}>
          completely attenuated the backwall response underneath it.{' '}
        </ColorSpan>
        This signal morphology is typical of HIC.
      </Text>
    </div>
  )
}

const DRA1Item2: FC<{ data: number[][] }> = (props) => {
  const { data } = props

  const shape1 = {
    type: 'rect' as const,
    x0: 120,
    x1: 230,
    y0: 24,
    y1: 180,
    line: { color: GeckoTheme.colors.SeaGreen },
  }

  const shape2 = {
    type: 'line' as const,
    x0: 0,
    x1: data ? data.length : 0,
    y0: 90,
    y1: 90,
    line: { color: GeckoTheme.colors.BrightOrange },
  }

  return (
    <div>
      <WaterfallPlot
        dataValues={data}
        width={310}
        height={500}
        shapes={[shape1, shape2]}></WaterfallPlot>
      <Text>
        <ColorSpan color={GeckoTheme.colors.SeaGreen}>The cracking </ColorSpan>
        is visible in the 45° B-Scan. It connects to the{' '}
        <ColorSpan color={GeckoTheme.colors.BrightOrange}> ID </ColorSpan>
        and completely attenutates the ID response behind it. This is strongly
        indicative of ID connected HIC damage.
      </Text>
    </div>
  )
}

const DRA1VerdictItem: FC = () => {
  return (
    <div>
      <AntTitle level={4}>
        Gecko Verdict:{' '}
        <ColorSpan color={GeckoTheme.colors.BrightOrange}>
          DRA Level 5
        </ColorSpan>
      </AntTitle>
      <Text strong>
        Based on the above notes, this indication is ID connected HIC.
      </Text>
    </div>
  )
}

const DRA1Actions: FC = () => {
  return (
    <div>
      <AntTitle level={4}>
        Recommended Action:{' '}
        <ColorSpan color={GeckoTheme.colors.LemonYellow}>FFS</ColorSpan>
      </AntTitle>
      <Text strong>
        There is strong evidence of ID connected HIC. Gecko recommends running
        FFS to determine if the asset is still safe to operate.
      </Text>
      <GridContainer>
        <Button onClick={() => message.success('Action Accepted!')}>
          Accept
        </Button>
        <Button>Reject</Button>
      </GridContainer>
    </div>
  )
}

// DRA 1 END

const DRA2View: FC<DRAViewProps> = (props) => {
  const { bscan0, bscan2 } = props

  const items: CollapseProps['items'] = [
    {
      key: '1',
      label: 'No backwall response in the 0°',
      children: <DRA2Item1 data={bscan0}></DRA2Item1>,
    },
    {
      key: '2',
      label: 'Crack with attenutation in the 45°',
      children: <DRA2Item2 data={bscan2}></DRA2Item2>,
    },
  ]

  return (
    <ColumnWrapper>
      <AntTitle level={4}>Observed Qualities</AntTitle>
      <Collapse items={items} accordion />
      <Divider></Divider>
      <DRA2VerdictItem></DRA2VerdictItem>
      <Divider></Divider>
      <DRA2Actions></DRA2Actions>
    </ColumnWrapper>
  )
}

const DRA2Item1: FC<{ data: number[][] }> = (props) => {
  const { data } = props

  const shape1 = {
    type: 'rect' as const,
    x0: 4,
    x1: 14,
    y0: 215,
    y1: 253,
    line: { color: GeckoTheme.colors.SeaGreen },
  }

  const shape2 = {
    type: 'line' as const,
    x0: 0,
    x1: data ? data.length : 0,
    y0: 205,
    y1: 205,
    line: { color: GeckoTheme.colors.BrightOrange },
  }

  return (
    <div>
      <WaterfallPlot
        dataValues={data}
        width={310}
        height={500}
        shapes={[shape1, shape2]}></WaterfallPlot>
      <Text>
        <ColorSpan color={GeckoTheme.colors.SeaGreen}>The blister </ColorSpan>
        has{' '}
        <ColorSpan color={GeckoTheme.colors.BrightOrange}>
          completely attenuated the backwall response underneath it.{' '}
        </ColorSpan>
        This signal morphology is typical of HIC.
      </Text>
    </div>
  )
}

const DRA2Item2: FC<{ data: number[][] }> = (props) => {
  const { data } = props

  const shape1 = {
    type: 'rect' as const,
    x0: 50,
    x1: 140,
    y0: 100,
    y1: 180,
    line: { color: GeckoTheme.colors.SeaGreen },
  }

  const shape2 = {
    type: 'line' as const,
    x0: 0,
    x1: data ? data.length : 0,
    y0: 90,
    y1: 90,
    line: { color: GeckoTheme.colors.BrightOrange },
  }

  return (
    <div>
      <WaterfallPlot
        dataValues={data}
        width={310}
        height={500}
        shapes={[shape1, shape2]}></WaterfallPlot>
      <Text>
        <ColorSpan color={GeckoTheme.colors.SeaGreen}>The cracking </ColorSpan>
        is visible in the 45° B-Scan, but it does not connect to{' '}
        <ColorSpan color={GeckoTheme.colors.BrightOrange}>the ID </ColorSpan>
        and does not completely attenutate the response behind it. This is
        indicative of HIC damage, but not ID-connected HIC damage.
      </Text>
    </div>
  )
}

const DRA2VerdictItem: FC = () => {
  return (
    <div>
      <AntTitle level={4}>
        Gecko Verdict:{' '}
        <ColorSpan color={GeckoTheme.colors.BrightOrange}>
          DRA Level 4
        </ColorSpan>
      </AntTitle>
      <Text strong>Based on the above notes, this indication is HIC.</Text>
    </div>
  )
}

const DRA2Actions: FC = () => {
  return (
    <div>
      <AntTitle level={4}>
        Recommended Action:{' '}
        <ColorSpan color={GeckoTheme.colors.LemonYellow}>FFS</ColorSpan>
      </AntTitle>
      <Text strong>
        There is strong evidence of HIC. Gecko recommends running FFS to
        determine if the asset is still safe to operate.
      </Text>
      <GridContainer>
        <Button onClick={() => message.success('Action Accepted!')}>
          Accept
        </Button>
        <Button>Reject</Button>
      </GridContainer>
    </div>
  )
}

// DRA 2 END

const DRA3View: FC<DRAViewProps> = (props) => {
  const { bscan0, bscan2 } = props

  const items: CollapseProps['items'] = [
    {
      key: '1',
      label: 'No backwall response in the 0°',
      children: <DRA3Item1 data={bscan0}></DRA3Item1>,
    },
    {
      key: '2',
      label: 'Connection to inner diameter in the 45°',
      children: <DRA3Item2 data={bscan2}></DRA3Item2>,
    },
  ]

  return (
    <ColumnWrapper>
      <AntTitle level={4}>Observed Qualities</AntTitle>
      <Collapse items={items} accordion />
      <Divider></Divider>
      <DRA3VerdictItem></DRA3VerdictItem>
      <Divider></Divider>
      <DRA3Actions></DRA3Actions>
    </ColumnWrapper>
  )
}

const DRA3Item1: FC<{ data: number[][] }> = (props) => {
  const { data } = props

  const shape1 = {
    type: 'rect' as const,
    x0: 5,
    x1: 27,
    y0: 238,
    y1: 264,
    line: { color: GeckoTheme.colors.SeaGreen },
  }

  const shape2 = {
    type: 'line' as const,
    x0: 0,
    x1: data ? data.length : 0,
    y0: 205,
    y1: 205,
    line: { color: GeckoTheme.colors.BrightOrange },
  }

  return (
    <div>
      <WaterfallPlot
        dataValues={data}
        width={305}
        height={500}
        shapes={[shape1, shape2]}></WaterfallPlot>
      <Text>
        <ColorSpan color={GeckoTheme.colors.SeaGreen}>The blister </ColorSpan>
        has{' '}
        <ColorSpan color={GeckoTheme.colors.BrightOrange}>
          completely attenuated the backwall response underneath it.{' '}
        </ColorSpan>
        This signal morphology is typical of HIC.
      </Text>
    </div>
  )
}

const DRA3Item2: FC<{ data: number[][] }> = (props) => {
  const { data } = props

  const shape1 = {
    type: 'rect' as const,
    x0: 130,
    x1: 430,
    y0: 5,
    y1: 180,
    line: { color: GeckoTheme.colors.SeaGreen },
  }

  const shape2 = {
    type: 'line' as const,
    x0: 0,
    x1: data ? data.length : 0,
    y0: 85,
    y1: 85,
    line: { color: GeckoTheme.colors.BrightOrange },
  }

  return (
    <div>
      <WaterfallPlot
        dataValues={data}
        width={300}
        height={500}
        shapes={[shape1, shape2]}></WaterfallPlot>
      <Text>
        <ColorSpan color={GeckoTheme.colors.SeaGreen}>The cracking </ColorSpan>
        is visible in the 45° B-Scan. It connects to the
        <ColorSpan color={GeckoTheme.colors.BrightOrange}> ID </ColorSpan>and
        completely attenutates the ID response behind it. This is strongly
        indicative of ID-connected HIC damage.
      </Text>
    </div>
  )
}

const DRA3VerdictItem: FC = () => {
  return (
    <div>
      <AntTitle level={4}>
        Gecko Verdict:{' '}
        <ColorSpan color={GeckoTheme.colors.BrightOrange}>
          DRA Level 5
        </ColorSpan>
      </AntTitle>
      <Text strong>
        Based on the above notes, this indication is ID connected HIC.
      </Text>
    </div>
  )
}

const DRA3Actions: FC = () => {
  return (
    <div>
      <AntTitle level={4}>
        Recommended Action:{' '}
        <ColorSpan color={GeckoTheme.colors.LemonYellow}>FFS</ColorSpan>
      </AntTitle>
      <Text strong>
        There is strong evidence of ID-connected HIC. Gecko recommends running
        FFS to determine if the asset is still safe to operate.
      </Text>
      <GridContainer>
        <Button onClick={() => message.success('Action Accepted!')}>
          Accept
        </Button>
        <Button>Reject</Button>
      </GridContainer>
    </div>
  )
}

// DRA 3 END

export const Title = () => 'TriLat Plots'
export const Width = '24rem'
export const CardView: FC<IndicationDetailCardProps> = () => {
  const { indication: item } = useIndicationData()

  const { data, isLoading } = useIndicationFiles(item?.id)

  return (
    <div>
      {item?.id === 'd28a7fcce8fbfe0704c3037ba0d0e930' && (
        <div>
          {!_.isNil(data) && !isLoading && (
            <Spin
              indicator={<LoadingOutlined />}
              size='large'
              spinning={isLoading}
              style={{ minHeight: '50vh' }}>
              <DRA1View
                bscan0={data['bscan_0']}
                bscan1={data['bscan_1']}
                bscan2={data['bscan_2']}
              />
            </Spin>
          )}
        </div>
      )}
      {item?.id === '9d78e61ae312fa0bdd4d40177f9566ca' && (
        <div>
          {!_.isNil(data) && !isLoading && (
            <Spin
              indicator={<LoadingOutlined />}
              size='large'
              spinning={isLoading}
              style={{ minHeight: '50vh' }}>
              <DRA2View
                bscan0={data['bscan_0']}
                bscan1={data['bscan_1']}
                bscan2={data['bscan_2']}
              />
            </Spin>
          )}
        </div>
      )}
      {item?.id === 'a2ce1f20b4a15b9ec7a7df50673bd7a6' && (
        <div>
          {!_.isNil(data) && !isLoading && (
            <Spin
              indicator={<LoadingOutlined />}
              size='large'
              spinning={isLoading}
              style={{ minHeight: '50vh' }}>
              <DRA3View
                bscan0={data['bscan_0']}
                bscan1={data['bscan_1']}
                bscan2={data['bscan_2']}
              />
            </Spin>
          )}
        </div>
      )}
    </div>
  )
}

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: repeat(4, max-content);
  align-items: center;
  padding-top: 8px;
  gap: 2px 8%;
`
const ColumnWrapper = styled.div`
  display: flex;
  flex-direction: column; /* Arrange children vertically */
  gap: 10px; /* Add space between each child (div) */
  padding: 10px; /* Optional: Add some padding inside the container */
`

const ColorSpan = styled.span<{ color: string }>`
  color: ${({ color }) => color};
`
